import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_BASE_URL } from "../../../config";

const POLL_MS = 10000;

// Vector 2026-09-25: หลอด/บด ต้องไม่ติดกับคะแนน แต่ช่องว่างเปล่าดูเหมือนตารางขาด
// ⇒ แยกด้วยเส้นหนาแทน หัวคอลัมน์หลอด/บดพื้นเทาอ่อน
// Vector 2026-09-25 12:59: "ตัวเลขไม่ต้องหนา ... ตัวเลขต้องเท่ากันห้ามเน้น" ⇒ ตัวเลขทุกช่องขนาด/น้ำหนักเท่ากัน
// Vector 2026-09-25 13:02: "ตัวเลขหลอด บด สีเทาดีแล้ว" ⇒ หลอด/บด ต่างแค่สีเทา
const SPLIT_TD = { color: "#6c757d" };
const SPLIT_EDGE = { borderLeft: "3px solid #adb5bd" };
const SPLIT_TH = { backgroundColor: "#7a818c", fontSize: "12px", fontWeight: "normal" };

// Pure: per-seller totals from raw showdata/user rows.
// score keeps the original all-codes sum/order; tube/crushed split out iceCode "1I"/"2I" prefixes (case-insensitive).
export const buildLeaderboard = (raw) => {
    const totals = new Map();
    (raw || []).forEach(entry => {
        const seller = entry.nameuser || "Unknown";
        const prev = totals.get(seller) || { score: 0, tube: 0, crushed: 0 };
        (entry.Normalize_Sale || []).forEach(item => {
            const qty = Number(item?.quantity ?? 0) || 0;
            const code = (item?.iceCode || "").toUpperCase();
            prev.score += qty;
            if (code.startsWith("1I")) prev.tube += qty;
            else if (code.startsWith("2I")) prev.crushed += qty;
        });
        totals.set(seller, prev);
    });
    return Array.from(totals.entries())
        .sort(([, a], [, b]) => b.score - a.score)
        .map(([usertype, vals], idx) => ({ rank: idx + 1, usertype, ...vals }));
};

const LeaderboardCard = ({ startDate, endDate, sellerId, sellerIds, onLogout, onSendToDisplay }) => {
    const [scores, setScores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showAll, setShowAll] = useState(true);
    const pollRef = useRef(null);
    const isFirstRef = useRef(true);

    const getToken = useCallback(() => {
        const user = localStorage.getItem("user");
        if (!user) { onLogout?.(); return null; }
        return JSON.parse(user)?.token;
    }, [onLogout]);

    const fetchScores = useCallback(async (hard = false) => {
        const token = getToken();
        if (!token) return;
        if (hard || isFirstRef.current) setLoading(true);
        try {
            const payload = { start_date: startDate, end_date: endDate };
            if (sellerIds?.length) payload.seller_ids = sellerIds;
            else if (sellerId) payload.seller_id = sellerId;

            const res = await fetch(`${API_BASE_URL}/saledata/api/data/showdata/user`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(res.statusText);

            const rawJson = await res.json();
            const raw = Array.isArray(rawJson) ? rawJson : (Array.isArray(rawJson?.data) ? rawJson.data : []);

            setScores(buildLeaderboard(raw));
            setError("");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            isFirstRef.current = false;
        }
    }, [getToken, startDate, endDate, sellerId, sellerIds, onLogout]);

    useEffect(() => {
        fetchScores(true);
        pollRef.current = setInterval(() => fetchScores(false), POLL_MS);
        return () => clearInterval(pollRef.current);
    }, [fetchScores]);

    const visible = showAll ? scores : scores.slice(0, 3);

    return (
        <div className="btc-table-container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px", minHeight: "30px" }}>
                <strong style={{ fontSize: "13px" }}>ลำดับคะแนน</strong>
                <div style={{ display: "flex", gap: "6px" }}>
                    {onSendToDisplay && (
                        <button className="btc-btn btn-sm" onClick={() => onSendToDisplay("leaderboard", scores)}
                            style={{ padding: "4px 8px", fontSize: "12px", background: "#1976d2", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                            📺 ส่งไปแสดงผล
                        </button>
                    )}
                    {scores.length > 3 && (
                        <button className="btc-btn btn-sm" onClick={() => setShowAll(v => !v)}
                            style={{ padding: "4px 8px", fontSize: "12px" }}>
                            {showAll ? "ย่อ" : "ดูทั้งหมด"}
                        </button>
                    )}
                </div>
            </div>
            {loading ? <p>Loading...</p> : error ? <p className="btc-error">{error}</p> : (
                <table className="btc-table">
                    <thead><tr><th>ลำดับ</th><th>สาย</th><th>คะแนน</th><th style={{ ...SPLIT_TH, ...SPLIT_EDGE }}>หลอด</th><th style={SPLIT_TH}>บด</th></tr></thead>
                    <tbody>
                        {visible.map((item, i) => (
                            <tr key={i}><td>{item.rank}</td><td>{item.usertype}</td><td>{item.score}</td><td style={{ ...SPLIT_TD, ...SPLIT_EDGE }}>{item.tube}</td><td style={SPLIT_TD}>{item.crushed}</td></tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default LeaderboardCard;
